import React, { useCallback, useMemo, useRef } from 'react';
import Chart from 'react-apexcharts';
import { useScreenshot } from 'use-react-screenshot';
import {
  mapCppRowToPerJob,
  isValidCppData,
  getVisibleHetrojobs,
  buildPerJobFromScenarioData,
} from './heterojobChartUtils';

const colorMap = {
  CAPEX: '#3996FF',
  Recurring: '#B0D5FF',
  Material: '#A1A19D',
  Consumables: '#D5D5D3',
};

const SingleScenarioChart = ({ onCapture, costData, cppData, currency = '' }) => {
  const chartRef = useRef(null);
  const [, takeScreenshot] = useScreenshot();

  const chartValues = useMemo(() => {
    const capex = parseFloat(costData?.system_cost_per_part?.value || 0);
    const recurring = parseFloat(costData?.recurring_cost_per_part?.value || 0);
    const material = parseFloat(costData?.material_cost_per_part?.value || 0);
    const consumables = parseFloat(costData?.cpp_consumables?.value || 0);
    const total = costData?.total_am_cpp?.value
      ? parseFloat(costData.total_am_cpp.value)
      : capex + recurring + material + consumables;
    return { capex, recurring, material, consumables, total };
  }, [costData]);

  const visibleHetrojobs = useMemo(
    () => getVisibleHetrojobs(costData?.hetrojobs),
    [costData?.hetrojobs]
  );

  const perJobValues = useMemo(() => {
    if (visibleHetrojobs.length === 0) return null;
    if (isValidCppData(cppData, visibleHetrojobs.length)) {
      return cppData.map((row) => mapCppRowToPerJob(row)).filter(Boolean);
    }
    return buildPerJobFromScenarioData(costData, visibleHetrojobs);
  }, [costData, visibleHetrojobs, cppData]);

  const calculateYAxisMax = useCallback((total, minValue) => {
    if (!Number.isFinite(total) || total <= 0) {
      return 20;
    }
    
    const buffer = Math.max(total * 0.02, minValue || 0);
    const adjustedTotal = total + buffer;
    const magnitude = Math.pow(10, Math.floor(Math.log10(adjustedTotal)));
    const normalized = adjustedTotal / magnitude;
    
    let rounded;
    if (normalized <= 1.1) {
      rounded = 1.2;
    } else if (normalized <= 1.2) {
      rounded = 1.2;
    } else if (normalized <= 1.3) {
      rounded = 1.3;
    } else if (normalized <= 1.4) {
      rounded = 1.4;
    } else if (normalized <= 1.5) {
      rounded = 1.5;
    } else if (normalized <= 2) {
      rounded = 2;
    } else if (normalized <= 2.5) {
      rounded = 2.5;
    } else if (normalized <= 5) {
      rounded = 5;
    } else {
      rounded = 10;
    }
    
    let maxValue = rounded * magnitude;
    const maxAllowed = adjustedTotal * 1.1;
    if (maxValue > maxAllowed) {
      const smallerMagnitude = Math.pow(10, Math.floor(Math.log10(adjustedTotal * 1.1)));
      const normalizedMax = (adjustedTotal * 1.1) / smallerMagnitude;
      
      if (normalizedMax <= 1.2) {
        maxValue = 1.2 * smallerMagnitude;
      } else if (normalizedMax <= 1.5) {
        maxValue = 1.5 * smallerMagnitude;
      } else if (normalizedMax <= 2) {
        maxValue = 2 * smallerMagnitude;
      } else if (normalizedMax <= 2.5) {
        maxValue = 2.5 * smallerMagnitude;
      } else if (normalizedMax <= 5) {
        maxValue = 5 * smallerMagnitude;
      } else {
        maxValue = 10 * smallerMagnitude;
      }
    }
    
    return maxValue;
  }, []);

  const minNonZeroValue = useMemo(() => {
    if (perJobValues?.length > 0) {
      const all = perJobValues.flatMap((j) => [j.capex, j.recurring, j.material, j.consumables]).filter((v) => v > 0);
      return all.length > 0 ? Math.min(...all) : 0;
    }
    const values = [chartValues.capex, chartValues.recurring, chartValues.material, chartValues.consumables].filter((v) => v > 0);
    return values.length > 0 ? Math.min(...values) : 0;
  }, [chartValues, perJobValues]);

  const calculateAdjustedValues = useCallback((values, total) => {
    const minVisualHeight = Math.max(total * 0.02, 0.1);
    let totalAdjustment = 0;
    const adjustedValues = values.map(item => {
      if (item.value > 0 && item.value < minVisualHeight) {
        const adjustment = minVisualHeight - item.value;
        totalAdjustment += adjustment;
        return { ...item, adjusted: minVisualHeight };
      }
      return { ...item, adjusted: item.value };
    });
    
    if (totalAdjustment > 0) {
      const largeSegments = adjustedValues.filter(v => v.value >= minVisualHeight);
      const largeSegmentsTotal = largeSegments.reduce((sum, v) => sum + v.value, 0);
      
      if (largeSegmentsTotal > 0) {
        largeSegments.forEach(largeSegment => {
          const ratio = largeSegment.value / largeSegmentsTotal;
          const adjustment = totalAdjustment * ratio;
          const item = adjustedValues.find(v => v.name === largeSegment.name);
          if (item) {
            item.adjusted = item.value - adjustment;
          }
        });
      }
    }
    
    return adjustedValues;
  }, []);

  const xAxisCategories = useMemo(() => {
    if (visibleHetrojobs.length > 0) {
      return visibleHetrojobs.map((_, index) => `Part ${index + 1}`);
    }

    // Default behaviour: single part
    return ['Part 1'];
  }, [visibleHetrojobs]);

  const effectiveTotal = perJobValues?.length ? Math.max(...perJobValues.map((j) => j.total), 0) : chartValues.total;
  const yAxisMax = useMemo(
    () => calculateYAxisMax(effectiveTotal, minNonZeroValue),
    [effectiveTotal, minNonZeroValue, calculateYAxisMax]
  );
  const totalsPerBar = perJobValues ? perJobValues.map((j) => j.total) : [chartValues.total];

  const sortedAdjustedValues = useMemo(() => {
    const values = [
      { name: 'CAPEX', value: chartValues.capex || 0 },
      { name: 'Recurring', value: chartValues.recurring || 0 },
      { name: 'Material', value: chartValues.material || 0 },
      { name: 'Consumables', value: chartValues.consumables || 0 },
    ];
    
    const total = values.reduce((sum, item) => sum + item.value, 0);
    const adjustedValues = calculateAdjustedValues(values, total);
    
    return [...adjustedValues]
      .filter(item => item.value > 0)
      .sort((a, b) => b.adjusted - a.adjusted);
  }, [chartValues, calculateAdjustedValues]);

  const capture = useCallback(async () => {
    const target = chartRef.current;
    if (!target) {
      return;
    }
    const img = await takeScreenshot(target);
    if (img && onCapture) {
      onCapture(img);
    }
  }, [takeScreenshot, onCapture]);

  const options = useMemo(
    () => ({
      chart: {
        id: 'cost-per-part-single',
        stacked: true,
        toolbar: { show: false },
        zoom: { enabled: false },
        events: {
          mounted: capture,
          updated: capture,
        },
      },
      colors:
        perJobValues?.length > 0
          ? ['CAPEX', 'Recurring', 'Material', 'Consumables']
              .filter((name) => perJobValues.some((j) => (j[name.toLowerCase()] || 0) > 0))
              .map((name) => colorMap[name] || '#3F8FF1')
          : sortedAdjustedValues.map((item) => colorMap[item.name] || '#3F8FF1'),
      plotOptions: {
        bar: {
          horizontal: false,
          columnWidth: '45%',
          endingShape: 'flat',
          dataLabels: {
            total: {
              enabled: true,
              formatter: (_val, opts) => {
                const idx = opts?.dataPointIndex ?? 0;
                const total = totalsPerBar[idx];
                return total != null && Number.isFinite(total) ? `${total.toFixed(2)}${currency ? ` ${currency}` : ''}` : '';
              },
              style: {
                fontSize: '12px',
                fontWeight: 600,
                colors: ['#1f1f1f'],
              },
            },
          },
        },
      },
      dataLabels: {
        enabled: false,
      },
      stroke: {
        show: true,
        width: 1,
        colors: ['#ffffff'],
      },
      xaxis: {
        categories: xAxisCategories,
        axisBorder: { show: true, color: '#CFCFCF' },
        labels: {
          style: {
            colors: 'black',
            fontSize: '14px',
            fontWeight: 700,
          },
        },
      },
      yaxis: {
        min: 0,
        max: yAxisMax,
        tickAmount: 5,
        labels: {
          formatter: (val) => (Number.isFinite(val) ? val.toFixed(0) : '0'),
          style: {
            colors: '#575757',
            fontSize: '14px',
          },
        },
        title: {
          text: 'Total AM Cost per Part',
          style: {
            fontSize: '14px',
            fontWeight: 700,
            color: 'black',
          },
        },
      },
      legend: {
        position: 'right',
        offsetY: 0,
        fontSize: '11px',
      },
      grid: {
        borderColor: '#E0E0E0',
        strokeDashArray: 3,
        yaxis: {
          lines: { show: true },
        },
      },
      tooltip: {
        y: {
          formatter: (val) => {
            return val.toFixed(2);
          },
        },
      },
    }),
    [capture, yAxisMax, sortedAdjustedValues, currency, xAxisCategories, totalsPerBar, perJobValues],
  );

  const series = useMemo(() => {
    if (perJobValues?.length > 0) {
      const componentOrder = [
        { key: 'capex', name: 'CAPEX' },
        { key: 'recurring', name: 'Recurring' },
        { key: 'material', name: 'Material' },
        { key: 'consumables', name: 'Consumables' },
      ];
      return componentOrder
        .map(({ key, name }) => ({ name, data: perJobValues.map((j) => j[key] || 0) }))
        .filter((s) => s.data.some((v) => v > 0));
    }
    const numberOfBars = visibleHetrojobs.length > 0 ? visibleHetrojobs.length : 1;
    return sortedAdjustedValues.map((item) => ({
      name: item.name,
      data: Array(numberOfBars).fill(item.adjusted),
    }));
  }, [sortedAdjustedValues, visibleHetrojobs, perJobValues]);

  return (
    <div
      ref={chartRef}
      aria-hidden
      style={{
        position: 'absolute',
        top: '-9999px',
        left: '-9999px',
        background: '#fff',
        pointerEvents: 'none',
        boxSizing: 'border-box',
      }}
    >
      <Chart options={options} series={series} type="bar" width={650} height={400} />
    </div>
  );
};

export default SingleScenarioChart;
